import React, { useState, useEffect, useRef} from "react";
import { CardElement, useStripe, useElements } from "@stripe/react-stripe-js";
import { Formik, Form, useField, Field } from 'formik';
import * as Yup from 'yup';
import * as PAGE from '../../Constants/Page.js'

const MyTextInput = ({ label, ...props }) => {
    // useField() returns [formik.getFieldProps(), formik.getFieldMeta()]
    // which we can spread on <input>. We can use field meta to show an error
    // message if the field is invalid and it has been touched (i.e. visited)
    const [field, meta] = useField(props);
    return (
        <div className='form-group'>
            <label htmlFor={props.id || props.name}>{label}</label>
            <input className="text-input form-control" {...field} {...props} />
            {meta.touched && meta.error ? (
                <div className="error">{meta.error}</div>
            ) : null}
        </div>
    );
};

const MyTextarea = ({ label, ...props }) => {
    // useField() returns [formik.getFieldProps(), formik.getFieldMeta()]
    // which we can spread on <input>. We can use field meta to show an error
    // message if the field is invalid and it has been touched (i.e. visited)
    const [field, meta] = useField(props);
    return (
        <div className='form-group'>
            <label htmlFor={props.id || props.name}>{label}</label>
            <textarea className="text-input form-control" {...field} {...props} />
            {meta.touched && meta.error ? (
                <div className="error">{meta.error}</div>
            ) : null}
        </div>
    );
};


export default function CheckoutForm(props) {
    const [succeeded, setSucceeded] = useState(false);
    const [error, setError] = useState(null);
    const [processing, setProcessing] = useState('');
    const [disabled, setDisabled] = useState(true);
    const [clientSecret, setClientSecret] = useState('');
    const [email, setEmail] = useState('');
    const [reservationId, setReservationId] = useState(0);
    const [payload, setPayload] = useState('');
    const stripe = useStripe();
    const elements = useElements();
    const intentCreatedRef = useRef(false);

    useEffect(() => {
        if (intentCreatedRef.current) return;
        intentCreatedRef.current = true;

        let cancelled = false;

        async function createIntent() {
            try {
                if (props.reservation.payNow === 0) {
                    const url = `api/setupintent?propertyKey=${encodeURIComponent(props.reservation.propertyKey)}&paymentKey=${encodeURIComponent(props.reservation.paymentKey)}`;
                    console.log("Calling:", url);

                    const res = await fetch(url);

                    console.log("SetupIntent status:", res.status);

                    if (!res.ok) {
                        throw new Error(`SetupIntent HTTP ${res.status}`);
                    }

                    const data = await res.json();
                    console.log("SetupIntent response:", data);

                    if (!cancelled) {
                        setClientSecret(data.clientSecret);
                    }
                } else {
                    const url = `api/paymentintentapi`;
                    console.log("Calling:", url, props.reservation);

                    const res = await fetch(url, {
                        method: "POST",
                        headers: {
                            "Content-Type": "application/json"
                        },
                        body: JSON.stringify(props.reservation)
                    });

                    console.log("PaymentIntent status:", res.status);

                    if (!res.ok) {
                        throw new Error(`PaymentIntent HTTP ${res.status}`);
                    }

                    const data = await res.json();
                    console.log("PaymentIntent response:", data);

                    if (!cancelled) {
                        setClientSecret(data.clientSecret);
                    }
                }
            } catch (error) {
                console.error("Intent creation failed:", error);

                if (!cancelled) {
                    alert("Its been a while so availability may have changed. We have updated your search so please select your room preference again.");
                    window.location.reload();
                }
            }
        }

        createIntent();

        return () => {
            cancelled = true;
        };
    }, []);

    const phoneRegExp = /^((\\+[1-9]{1,4}[ \\-]*)|(\\([0-9]{2,3}\\)[ \\-]*)|([0-9]{2,4})[ \\-]*)*?[0-9]{3,4}?[ \\-]*[0-9]{3,4}?$/

    const cancelReservation = async (resId) => {
        
        let url = 'api/reservations/' + resId;

        fetch(url, {
            method: 'DELETE',
            
        })
            .then(() => {
                
            })
            .catch(error => {
                console.log('Reservation API Error: ' + error);
                
            });



    }

    const sendConfirmationEmail = async (resId) => {

        let url = 'api/reservations/SendConfirmationEmail/' + resId;

        fetch(url, {
            method: 'GET',

        })
            .then(() => {

            })
            .catch(error => {
                console.log('Reservation API Error: ' + error);

            });
    }

    const createReservation = async (values, payload) => {
        setEmail(values.email);

        let utm = {};

        try {
            utm = JSON.parse(localStorage.getItem('bookingUtm') || '{}');
        } catch {
            utm = {};
        }

        const reservation = {
            ...props.reservation,
            ...values,
            source: utm.source || null,
            medium: utm.medium || null,
            campaign: utm.campaign || null,
            referrer: utm.referrer || null,
            landingPage: utm.landingPage || null
        };

        let url = 'api/reservations';
        
        fetch(url, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json; charset=utf-8'
            },
            body: JSON.stringify(reservation)
        })
            .then((response) => {
                return response.json();
            })
            .then(data => {

                setReservationId(data);
                console.log('Reservation Created: ' + data.toString());
                if (payload.paymentIntent) {
                    updatePayment(data, payload);

                }
                else {
                    sendConfirmationEmail(data);
                    props.setPageState(PAGE.PAGE.SUCCESSFUL, data);
                }
                

            }).catch(error => {
                console.log('Reservation API Error: ' + error);
                setError('We were unable to complete your reservation. Please try again.');
                setProcessing(false);
                props.setPageState(PAGE.PAGE.FAIL, 'We were unable to complete your reservation. Please try again.')
        });



    }

    const cardStyle = {
        style: {
            base: {
                color: "#32325d",
                fontFamily: 'Arial, sans-serif',
                fontSmoothing: "antialiased",
                fontSize: "16px",
                "::placeholder": {
                    color: "#32325d"
                }
            },
            invalid: {
                color: "#fa755a",
                iconColor: "#fa755a"
            }
        },
        hidePostalCode: true
    };
    const handleChange = async (event) => {
        // Listen for changes in the CardElement
        // and display any errors as the customer types their card details
        setDisabled(event.empty);
        setError(event.error ? event.error.message : "");
    };

    const sessionIsAvailable = async () => {

        let url = 'api/session'

        try {
            const response = await fetch(url);

            if (!response.ok) {
                throw Error(response.statusText);
            }

            const isAvailable = await response.json();

            return isAvailable;


        } catch (error) {
            console.log(error);
            return false;
        }

    }

    const getIsAvailable = async () => {

        let url = 'api/availability?propertyKey=' + props.reservation.propertyKey + '&from=' + props.reservation.checkIn +
            '&nights=' + props.reservation.nights;

        try
        {
            const response = await fetch(url);

            if (!response.ok) {
                throw Error(response.statusText);
            }

            const available = await response.json();

            let returnValue = true;

            props.reservation.selectedRooms.forEach(function (room) {

                if (!available.includes(room.roomTypeId)) { returnValue = false; }

            });

            return returnValue;


        } catch (error) {
            console.log(error);
            return false;
        }
    }

    const handleCardSubmit = async (values) => {
        //ev.preventDefault();
        setProcessing(true);

        //check rooms are still available
        const isAvailable = await getIsAvailable();

        if (!isAvailable) {

            setError('We were unable to complete your reservation. Please try again.');
            setProcessing(false);
            props.setPageState(PAGE.PAGE.FAIL, 'In the time passed since selecting your reservation, our room availablity has changed.  Please try to make your reservation again.')
        }
        else {
            
            //check session has not timed out
            const sessionAvailable = await sessionIsAvailable();

            if (!sessionAvailable) {
                //setError('We were unable to complete your reservation. Please try again.');
                setProcessing(false);
                props.setPageState(PAGE.PAGE.FAIL, 'In the time passed since selecting your reservation our room availablity may have changed. Your card has not been charged. Please try to make your reservation again.')
            }

            else {

            
        
                if (props.reservation.payNow === 0 )//selectedRooms[0].roomPrice.withinGracePeriod)
                {
                    const payload = await stripe.confirmCardSetup(clientSecret, {
                        payment_method: {
                            card: elements.getElement(CardElement),
                            billing_details: {
                                name: values.firstName + ' ' + values.lastName,
                                email: values.email,
                                phone: values.telephone,
                            },
                        }
                    });
                    if (payload.error) {
                        //reverse reservation
                        //cancelReservation(resId);
                        setError(`Payment failed ${payload.error.message}`);
                        setProcessing(false);
                        //props.setPageState(PAGE.PAGE.FAIL, payload.error.message)
                    } else {
               
                        setError(null);
                        setProcessing(false);
                        setSucceeded(true);
                        createReservation(values, payload);
                        //props.setPageState(PAGE.PAGE.SUCCESSFUL, resId);
                    }
                }

                else {
                    const payload = await stripe.confirmCardPayment(clientSecret, {
                        receipt_email: values.email,
                        payment_method: {
                            card: elements.getElement(CardElement)
                        }
                    });

                    if (payload.error) {
                    //reverse reservation
                        //cancelReservation(resId);
                        setError(`Payment failed ${payload.error.message}`);
                        setProcessing(false);
                        //props.setPageState(PAGE.PAGE.FAIL, payload.error.message)
                    } else {
                    //update payment

                        //setPayload(payload);
                        //updatePayment(resId, payload);
                        setError(null);
                        setProcessing(false);
                        setSucceeded(true);
                        createReservation(values, payload);
                        //props.setPageState(PAGE.PAGE.SUCCESSFUL, resId);
                    }
                }
            }
        }
        
    };

    const updatePayment = async (resId, payload) => {
                
        let url = 'api/payment?reservationId=' + resId
            + '&payment=' + payload.paymentIntent.amount
            + '&paymentIntentId=' + payload.paymentIntent.id
            + '&paymentMethod=' + payload.paymentIntent.payment_method;

        fetch(url)
            .then((response) => {
                return response.ok;
            })
            .then(data => {
                            
                console.log('Payment Recorded: ' + data.toString());
                sendConfirmationEmail(resId);
                props.setPageState(PAGE.PAGE.SUCCESSFUL, resId);
                return 'OK';
                
            }).catch(error => {
                console.log('Payment Error: ' + error);
                return 'Error';
            });
    }

    if (!clientSecret) {
        return <div>Loading payment...</div>;
    }

    return (
        <>
            
                <Formik
                    initialValues={
                        {

                            titleId: '-1',
                            firstName: props.reservation.firstName,
                            lastName: props.reservation.lastName,
                            arrivalTime: '2000-01-01T14:00:00',
                            email: props.reservation.email,
                            telephone: props.reservation.telephone,
                            guestInstructions: props.reservation.guestInstructions

                        }
                    }

                validationSchema={Yup.object().shape({
                    firstName: Yup.string()
                        .required("First name is required"),
                    lastName: Yup.string()
                        .required("Last name is required"),
                    email: Yup.string()
                        .email("Invalid email address format")
                        .required("Email is required"),
                    telephone: Yup.string().matches(phoneRegExp, 'Phone number is not valid')
                        .required("Phone Number is required"),
                    postcode: Yup.string()
                        .required("Postcode is required"),
                    })}

                    onSubmit={(values, { setSubmitting }) => {

                        //alert(JSON.stringify(values, null, 2));
                        setEmail(values.email);
                        //createReservation(values);
                        handleCardSubmit(values);
                        setSubmitting(false);
                        

                    }
                    }

                >
                    {props => (
                        <Form >
                            <div className='form-row'>
                                <div className='col-md-6'>
                                    <MyTextInput
                                        label='First Name'
                                        name='firstName'
                                    />
                                </div>
                                <div className='col-md-6'>
                                    <MyTextInput
                                        label='Last Name'
                                        name='lastName'
                                    />
                                </div>
                            </div>
                            <MyTextInput
                                label='Email'
                                name='email'
                            />
                            <div className='form-row'>
                                <div className='col-md-6'>
                                    <MyTextInput
                                        label='Mobile Phone'
                                        name='telephone'
                                    />
                                </div>
                                <div className='col-md-6'>
                                    <MyTextInput
                                        label='Postcode'
                                        name='postcode'
                                    />
                                </div>
                            </div>

                            <MyTextarea

                                label='Instructions'
                                name='guestInstructions'
                            />

                            <CardElement id="card-element" options={cardStyle} onChange={handleChange} />
                            <button
                                className="payment-button"
                                disabled={processing || disabled || succeeded}
                                id="submit" type='submit'
                            >
                                <span id="button-text">
                                    {processing ? (
                                        <div className="spinner" id="spinner"></div>
                                    ) : (
                                            "Reserve"
                                        )}
                                </span>
                            </button>
                            {/* Show any error that happens when processing the payment */}
                            {error && (
                                <div className="card-error" role="alert">
                                    {error}
                                </div>
                            )}
                            {/* Show a success message upon completion */}
                            <p className={succeeded ? "result-message" : "result-message hidden"}>
                                Card details accepted, please wait while we confirm your reservation.
                            
                        </p>

                        </Form>
                    )}
                </Formik>
            
        
        </>
    );
}
